/// <reference types="react-scripts" />

import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  FileText,
  Users,
  Sparkles,
  Database,
  ShoppingCart,
  Hexagon,
  UserCircle,
  type LucideIcon,
} from 'lucide-react';

import { authAPI, User } from '../api/api';
import { BRAND_ASSETS } from '../brand/assets';

interface CategoryLink {
  label: string;
  to: string;
}

interface FeatureLink {
  label: string;
  to: string;
  icon: LucideIcon;
}

const CATEGORY_LINKS: CategoryLink[] = [
  { label: 'Research', to: '/research' },
  { label: 'Education', to: '/about' },
  { label: 'Community', to: '/community' },
  { label: 'Impact', to: '/contact' },
];

const FEATURE_LINKS: FeatureLink[] = [
  { label: 'The Three Wisemen', to: '/ask', icon: Brain },
  { label: 'Peer-Reviewed Papers', to: '/research', icon: FileText },
  { label: 'Research Groups', to: '/community', icon: Users },
  {
    label: 'Consciousness & Psychedelics',
    to: '/research?category=consciousness',
    icon: Sparkles,
  },
  { label: 'Datasets & Resources', to: '/research', icon: Database },
  { label: 'Marketplace (TrussDit)', to: '/marketplace', icon: ShoppingCart },
  { label: 'NFTs & Memberships', to: '/payment', icon: Hexagon },
];

const UTILITY_LINKS: CategoryLink[] = [
  { label: 'Home', to: '/home' },
  { label: 'Profile', to: '/profile' },
  { label: 'Admin', to: '/admin' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Payment', to: '/payment' },
];

function linkMatchesPath(linkTo: string, pathname: string, search: string): boolean {
  const [path, query] = linkTo.split('?');
  if (pathname !== path) {
    return false;
  }
  if (!query) {
    return search === '' || search === '?';
  }
  return `?${query}` === search;
}

function isNavLinkActive(linkTo: string, pathname: string, search: string): boolean {
  if (linkTo === '/') {
    return pathname === '/';
  }
  return linkMatchesPath(linkTo, pathname, search);
}

interface NavbarProps {
  /** Force marketing auth chrome even when a token exists (tests). */
  forcePublic?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ forcePublic = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const hasSession = !forcePublic && Boolean(localStorage.getItem('token'));
  const isPublicNav = !hasSession;
  const brandTo = hasSession ? '/home' : '/';
  const utilityLinks = isPublicNav
    ? [
        { label: 'Home', to: '/' },
        { label: 'About', to: '/about' },
        { label: 'Contact', to: '/contact' },
      ]
    : UTILITY_LINKS;

  const mobileUtilityLinks = utilityLinks.filter(
    (link) => link.to !== '/admin' || currentUser?.is_admin
  );

  useEffect(() => {
    if (!hasSession) {
      setCurrentUser(null);
      return;
    }
    let cancelled = false;
    authAPI
      .getMe()
      .then((user) => {
        if (!cancelled) {
          setCurrentUser(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentUser(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hasSession, location.pathname]);

  useEffect(() => {
    if (!accountMenuOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [accountMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAccountMenuOpen(false);
    setCurrentUser(null);
    navigate('/login');
  };

  const closeMobile = () => setIsMobileMenuOpen(false);

  const accountLabel = currentUser?.name?.trim().split(/\s+/)[0] || 'Account';

  const isFeatureActive = (to: string) =>
    linkMatchesPath(to, location.pathname, location.search);

  return (
    <nav className="navbar wr-navbar">
      <div className="navbar-container wr-navbar__inner">
        <div className="wr-navbar__header">
          <motion.div className="navbar-brand" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Link
              to={brandTo}
              className="brand-link wr-brand-link"
              onClick={closeMobile}
              aria-label="WisemenResearch home"
            >
              <img src={BRAND_ASSETS.logo} alt="" className="wr-brand-icon" aria-hidden />
              <span className="wr-brand-wordmark">
                Wisemen<span className="wr-brand-accent">Research</span>
              </span>
              <div className="brand-glow" />
            </Link>
          </motion.div>

          {isPublicNav ? (
            <div className="wr-auth-actions wr-logout-desktop">
              <Link to="/login" className="wr-auth-link">
                Sign in
              </Link>
              <Link to="/register" className="btn btn-primary wr-auth-cta">
                Join
              </Link>
            </div>
          ) : (
            <div className="wr-account-menu wr-logout-desktop" ref={accountMenuRef}>
              <motion.button
                type="button"
                className="wr-account-trigger"
                onClick={() => setAccountMenuOpen((open) => !open)}
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
                aria-label="Account menu"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <UserCircle size={22} strokeWidth={1.5} aria-hidden />
                <span>{accountLabel}</span>
              </motion.button>
              <AnimatePresence>
                {accountMenuOpen && (
                  <motion.div
                    className="wr-account-dropdown"
                    role="menu"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                  >
                    {currentUser && (
                      <p className="wr-account-dropdown-meta">
                        {currentUser.name}
                        <span>{currentUser.email}</span>
                      </p>
                    )}
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="wr-account-dropdown-link"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/payment"
                      role="menuitem"
                      className="wr-account-dropdown-link"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Subscription
                    </Link>
                    {currentUser?.is_admin && (
                      <Link
                        to="/admin"
                        role="menuitem"
                        className="wr-account-dropdown-link"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className="wr-account-dropdown-link wr-account-dropdown-logout"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <motion.button
            className="mobile-menu-btn"
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
            whileTap={{ scale: 0.95 }}
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span />
              <span />
              <span />
            </span>
          </motion.button>
        </div>

        <p className="wr-tagline">Better questions. Deeper research. A higher perspective.</p>

        <div className="wr-category-nav desktop-nav" aria-label="Primary sections">
          {CATEGORY_LINKS.map(({ label, to }) => (
            <NavLink
              key={label}
              to={to}
              className={({ isActive }) => `wr-category-link${isActive ? ' active' : ''}`}
            >
              {label}
            </NavLink>
          ))}
        </div>

        <div className="wr-feature-nav desktop-nav" aria-label="Platform features">
          {FEATURE_LINKS.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={label}
              to={to}
              className={() =>
                `wr-feature-link${isFeatureActive(to) ? ' active' : ''}`
              }
              title={label}
            >
              <Icon size={22} strokeWidth={1.5} aria-hidden />
              <span className="wr-feature-label">{label}</span>
            </NavLink>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mobile-nav-content">
              <p className="wr-mobile-section-label">Sections</p>
              {CATEGORY_LINKS.map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  className={`mobile-nav-link${linkMatchesPath(to, location.pathname, location.search) ? ' active' : ''}`}
                  onClick={closeMobile}
                >
                  {label}
                </Link>
              ))}

              <p className="wr-mobile-section-label">Features</p>
              {FEATURE_LINKS.map(({ label, to, icon: Icon }) => (
                <Link
                  key={label}
                  to={to}
                  className={`mobile-nav-link wr-mobile-feature${isFeatureActive(to) ? ' active' : ''}`}
                  onClick={closeMobile}
                >
                  <Icon size={18} strokeWidth={1.75} aria-hidden />
                  {label}
                </Link>
              ))}

              <p className="wr-mobile-section-label">More</p>
              {mobileUtilityLinks.map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  className={`mobile-nav-link${isNavLinkActive(to, location.pathname, location.search) ? ' active' : ''}`}
                  onClick={closeMobile}
                >
                  {label}
                </Link>
              ))}

              {isPublicNav ? (
                <>
                  <Link
                    to="/login"
                    className="mobile-nav-link"
                    onClick={closeMobile}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="mobile-logout-btn"
                    style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
                    onClick={closeMobile}
                  >
                    Join
                  </Link>
                </>
              ) : (
                <motion.button
                  className="mobile-logout-btn"
                  onClick={handleLogout}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Logout
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
